var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { assertUserTypeOptional, checkUserType } from "healthex-common/checkusertype";
import { OrganizationDataDeliveryType } from "healthex-common/organization.dtos";
import { UserType } from "healthex-common/user.dtos";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED, ParameterizedRemoteData, RemoteData } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { FormModel } from "./FormModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
export class AdminDataSettingsViewModel {
    constructor(container) {
        this.dataAccessPermissionsFormModel = new FormModel({
            allowDataAccessForPatientIdentification: true,
            allowDataAccessForDataDelivery: true,
        }, []);
        this.dataDeliverySettingsFormModel = new FormModel({
            deliveryType: OrganizationDataDeliveryType.NONE,
            deliverUncompressedData: false,
            deliverConvertedFHIRData: false,
            optedOutOfBackgroundRefresh: false,
            s3BucketName: "",
            s3AssumeRoleArn: "",
            s3BucketPath: "{orgId}/{externalId}/{referenceId}/{dataType}/{projectId}/{timestamp}",
            s3BucketRegion: "",
            sftpHost: "",
            sftpPort: null,
            sftpUsername: "",
            sftpPassword: "",
            sftpPath: "{orgId}/{externalId}/{referenceId}/{dataType}/{projectId}/{timestamp}",
        }, ["s3BucketName", "s3BucketRegion", "s3AssumeRoleArn", "sftpHost", "sftpUsername", "sftpPassword"], (input) => {
            if (input.s3BucketPath !== undefined) {
                this.dataDeliverySettingsFormModel.clearError("s3BucketPath");
                if (input.s3BucketPath.trim().length > 0 && !validators.validDataDeliveryPath(input.s3BucketPath)) {
                    this.dataDeliverySettingsFormModel.addError("s3BucketPath", "Path must only contain allowed parameters: {orgId}, {externalId}, {healthexPatientId}, {referenceId}, {dataType}, {projectId}, {timestamp}, {retrievalId}");
                }
            }
            if (input.sftpPath !== undefined) {
                this.dataDeliverySettingsFormModel.clearError("sftpPath");
                if (input.sftpPath.trim().length > 0 && !validators.validDataDeliveryPath(input.sftpPath)) {
                    this.dataDeliverySettingsFormModel.addError("sftpPath", "Path must only contain allowed parameters: {orgId}, {externalId}, {healthexPatientId}, {referenceId}, {dataType}, {projectId}, {timestamp}, {retrievalId}");
                }
            }
        });
        this.awsDataDeliveryInstructionsDialog = new SimpleDialogModel();
        this.editingDataAccessPermissions = false;
        this.editingDataDeliverySettings = false;
        this.organizationsManager = container.organizationsManager;
        this.provisioningService = container.provisioningService;
        this.invalidationManager = container.invalidationManager;
        this.adminUIManager = container.adminUIManager;
        this.organizationData = new ParameterizedRemoteData(this.invalidationManager, () => this.organizationId, () => this.loadOrganization());
        this.awsDataDeliveryInfoData = new RemoteData(this.invalidationManager, () => this.loadAWSDataDeliveryInfo());
        reaction(() => this.adminUIManager.sectionToExpand, async () => {
            this.expandSectionIfNecessary();
        }, { fireImmediately: true });
        reaction(() => this.dataDeliverySettingsFormModel.input.deliveryType, async () => {
            this.dataDeliverySettingsFormModel.clearErrors();
        });
    }
    get organizationId() {
        return this.adminUIManager.organizationId;
    }
    get organization() {
        return this.organizationData.value;
    }
    get healthcareOrganization() {
        if (this.organizationData.value === NOT_LOADED)
            return NOT_LOADED;
        return this.organizationData.value ? checkUserType(this.organizationData.value, UserType.HEALTHCARE_ORGANIZATION) : null;
    }
    expandSectionIfNecessary() {
        if (this.adminUIManager.sectionToExpand === "ACCESS_PERMISSIONS") {
            this.toggleEditingDataAccessPermissions();
        }
        else if (this.adminUIManager.sectionToExpand === "DELIVERY") {
            this.toggleEditingDataDeliverySettings();
        }
    }
    async loadOrganization() {
        if (!this.organizationId)
            return null;
        return assertUserTypeOptional(await this.organizationsManager.loadOrganization(this.organizationId), UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
    }
    toggleEditingDataAccessPermissions() {
        this.editingDataAccessPermissions = !this.editingDataAccessPermissions;
        if (this.editingDataAccessPermissions) {
            void this.loadDataAccessPermissions();
        }
    }
    toggleEditingDataDeliverySettings() {
        this.editingDataDeliverySettings = !this.editingDataDeliverySettings;
        if (this.editingDataDeliverySettings) {
            void this.loadDataDeliverySettings();
        }
    }
    async loadDataDeliverySettings() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23;
        await this.organizationData.waitForValue();
        this.dataDeliverySettingsFormModel.clearErrors();
        if (this.organization === NOT_LOADED)
            return;
        const defaultPath = "{projectId}/{referenceId}/{dataType}";
        this.dataDeliverySettingsFormModel.updateInput({
            deliveryType: (_c = (_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.dataDeliverySettings) === null || _b === void 0 ? void 0 : _b.deliveryType) !== null && _c !== void 0 ? _c : null,
            deliverUncompressedData: (_f = (_e = (_d = this.organization) === null || _d === void 0 ? void 0 : _d.dataDeliverySettings) === null || _e === void 0 ? void 0 : _e.deliverUncompressedData) !== null && _f !== void 0 ? _f : false,
            deliverConvertedFHIRData: (_j = (_h = (_g = this.organization) === null || _g === void 0 ? void 0 : _g.dataDeliverySettings) === null || _h === void 0 ? void 0 : _h.deliverConvertedFHIRData) !== null && _j !== void 0 ? _j : false,
            optedOutOfBackgroundRefresh: (_m = (_l = (_k = this.organization) === null || _k === void 0 ? void 0 : _k.dataDeliverySettings) === null || _l === void 0 ? void 0 : _l.optedOutOfBackgroundRefresh) !== null && _m !== void 0 ? _m : false,
            s3BucketName: (_r = (_q = (_p = (_o = this.organization) === null || _o === void 0 ? void 0 : _o.dataDeliverySettings) === null || _p === void 0 ? void 0 : _p.s3Settings) === null || _q === void 0 ? void 0 : _q.s3BucketName) !== null && _r !== void 0 ? _r : "",
            s3AssumeRoleArn: (_v = (_u = (_t = (_s = this.organization) === null || _s === void 0 ? void 0 : _s.dataDeliverySettings) === null || _t === void 0 ? void 0 : _t.s3Settings) === null || _u === void 0 ? void 0 : _u.s3AssumeRoleArn) !== null && _v !== void 0 ? _v : "",
            s3BucketPath: (_z = (_y = (_x = (_w = this.organization) === null || _w === void 0 ? void 0 : _w.dataDeliverySettings) === null || _x === void 0 ? void 0 : _x.s3Settings) === null || _y === void 0 ? void 0 : _y.s3BucketPath) !== null && _z !== void 0 ? _z : defaultPath,
            s3BucketRegion: (_3 = (_2 = (_1 = (_0 = this.organization) === null || _0 === void 0 ? void 0 : _0.dataDeliverySettings) === null || _1 === void 0 ? void 0 : _1.s3Settings) === null || _2 === void 0 ? void 0 : _2.s3BucketRegion) !== null && _3 !== void 0 ? _3 : "",
            sftpHost: (_7 = (_6 = (_5 = (_4 = this.organization) === null || _4 === void 0 ? void 0 : _4.dataDeliverySettings) === null || _5 === void 0 ? void 0 : _5.sftpSettings) === null || _6 === void 0 ? void 0 : _6.sftpHost) !== null && _7 !== void 0 ? _7 : "",
            sftpPort: (_11 = (_10 = (_9 = (_8 = this.organization) === null || _8 === void 0 ? void 0 : _8.dataDeliverySettings) === null || _9 === void 0 ? void 0 : _9.sftpSettings) === null || _10 === void 0 ? void 0 : _10.sftpPort) !== null && _11 !== void 0 ? _11 : null,
            sftpUsername: (_15 = (_14 = (_13 = (_12 = this.organization) === null || _12 === void 0 ? void 0 : _12.dataDeliverySettings) === null || _13 === void 0 ? void 0 : _13.sftpSettings) === null || _14 === void 0 ? void 0 : _14.sftpUsername) !== null && _15 !== void 0 ? _15 : "",
            sftpPassword: (_19 = (_18 = (_17 = (_16 = this.organization) === null || _16 === void 0 ? void 0 : _16.dataDeliverySettings) === null || _17 === void 0 ? void 0 : _17.sftpSettings) === null || _18 === void 0 ? void 0 : _18.sftpPassword) !== null && _19 !== void 0 ? _19 : "",
            sftpPath: (_23 = (_22 = (_21 = (_20 = this.organization) === null || _20 === void 0 ? void 0 : _20.dataDeliverySettings) === null || _21 === void 0 ? void 0 : _21.sftpSettings) === null || _22 === void 0 ? void 0 : _22.sftpPath) !== null && _23 !== void 0 ? _23 : defaultPath,
        });
    }
    get showDataAccessPermissionsSection() {
        if (this.healthcareOrganization === NOT_LOADED || this.healthcareOrganization === null)
            return false;
        return true;
    }
    get showDataDeliverySettingsSection() {
        if (this.organization === NOT_LOADED || this.organization === null)
            return false;
        return this.organization.type === UserType.EXTERNAL_RESEARCHER;
    }
    async loadAWSDataDeliveryInfo() {
        return await this.provisioningService.getAWSDataDeliveryInfo();
    }
    get dataAccessPermissionsDescription() {
        var _a, _b, _c, _d;
        if (this.healthcareOrganization === NOT_LOADED)
            return null;
        if (((_a = this.healthcareOrganization) === null || _a === void 0 ? void 0 : _a.allowDataAccessForPatientIdentification) &&
            ((_b = this.healthcareOrganization) === null || _b === void 0 ? void 0 : _b.allowDataAccessForDataDelivery)) {
            return "Your organization currently allows data access for both patient identification and data delivery.";
        }
        else if ((_c = this.healthcareOrganization) === null || _c === void 0 ? void 0 : _c.allowDataAccessForPatientIdentification) {
            return "Your organization currently only allows data access for patient identification.";
        }
        else if ((_d = this.healthcareOrganization) === null || _d === void 0 ? void 0 : _d.allowDataAccessForDataDelivery) {
            return "Your organization currently only allows data access for data delivery.";
        }
        else {
            return "Your organization currently does not allow data access for either patient identification or data delivery.";
        }
    }
    get dataDeliverySettingsDescription() {
        var _a, _b, _c, _d, _e, _f;
        if (this.organization === NOT_LOADED)
            return null;
        if (((_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.dataDeliverySettings) === null || _b === void 0 ? void 0 : _b.deliveryType) === OrganizationDataDeliveryType.S3) {
            return "Your organization currently uses S3 for data delivery.";
        }
        else if (((_d = (_c = this.organization) === null || _c === void 0 ? void 0 : _c.dataDeliverySettings) === null || _d === void 0 ? void 0 : _d.deliveryType) === OrganizationDataDeliveryType.SFTP) {
            return "Your organization currently uses SFTP for data delivery.";
        }
        else if (((_f = (_e = this.organization) === null || _e === void 0 ? void 0 : _e.dataDeliverySettings) === null || _f === void 0 ? void 0 : _f.deliveryType) === OrganizationDataDeliveryType.NONE) {
            return "Your organization has disabled data delivery.";
        }
        else {
            return "Your organization has not configured a data delivery method.";
        }
    }
    async loadDataAccessPermissions() {
        var _a, _b, _c, _d;
        await this.organizationData.waitForValue();
        if (this.healthcareOrganization === NOT_LOADED)
            return;
        this.dataAccessPermissionsFormModel.clearErrors();
        this.dataAccessPermissionsFormModel.updateInput({
            allowDataAccessForPatientIdentification: (_b = (_a = this.healthcareOrganization) === null || _a === void 0 ? void 0 : _a.allowDataAccessForPatientIdentification) !== null && _b !== void 0 ? _b : true,
            allowDataAccessForDataDelivery: (_d = (_c = this.healthcareOrganization) === null || _c === void 0 ? void 0 : _c.allowDataAccessForDataDelivery) !== null && _d !== void 0 ? _d : true,
        });
    }
    async saveDataAccessPermissions() {
        if (!this.organizationId)
            return;
        const { allowDataAccessForPatientIdentification, allowDataAccessForDataDelivery } = this.dataAccessPermissionsFormModel.input;
        await this.organizationsManager.updateOrganizationDetails(this.organizationId, {
            type: UserType.HEALTHCARE_ORGANIZATION,
            allowDataAccessForPatientIdentification,
            allowDataAccessForDataDelivery,
        });
        await this.organizationData.waitForReload();
        this.editingDataAccessPermissions = false;
    }
    restoreDataAccessPermissionDefaults() {
        this.dataAccessPermissionsFormModel.updateInput({
            allowDataAccessForPatientIdentification: true,
            allowDataAccessForDataDelivery: true,
        });
    }
    async saveDataDeliverySettings() {
        var _a, _b, _c, _d;
        if (this.organization === NOT_LOADED || !this.organization)
            return;
        this.dataDeliverySettingsFormModel.clearErrors();
        const wasNotConfigured = ((_b = (_a = this.organization.dataDeliverySettings) === null || _a === void 0 ? void 0 : _a.deliveryType) !== null && _b !== void 0 ? _b : null) === null;
        this.dataDeliverySettingsFormModel.validateField("deliveryType", validators.notNull, "Select a delivery type");
        if (this.dataDeliverySettingsFormModel.input.deliveryType === OrganizationDataDeliveryType.S3) {
            this.dataDeliverySettingsFormModel.validateField("s3BucketName", validators.notBlank, "Enter a bucket name");
            this.dataDeliverySettingsFormModel.validateField("s3BucketRegion", validators.notBlank, "Enter a bucket region");
            this.dataDeliverySettingsFormModel.validateField("s3AssumeRoleArn", validators.notBlank, "Enter an IAM role ARN");
            this.dataDeliverySettingsFormModel.validateField("s3AssumeRoleArn", validators.validIAMRoleARN, "Enter a valid IAM role ARN");
            this.dataDeliverySettingsFormModel.validateField("s3BucketPath", validators.validDataDeliveryPath, "Path must only contain allowed parameters: {orgId}, {externalId}, {healthexPatientId}, {referenceId}, {dataType}, {projectId}, {timestamp}");
        }
        else if (this.dataDeliverySettingsFormModel.input.deliveryType === OrganizationDataDeliveryType.SFTP) {
            this.dataDeliverySettingsFormModel.validateField("sftpHost", validators.notBlank, "Enter a host");
            this.dataDeliverySettingsFormModel.validateField("sftpUsername", validators.notBlank, "Enter a username");
            this.dataDeliverySettingsFormModel.validateField("sftpPassword", validators.notBlank, "Enter a password");
            this.dataDeliverySettingsFormModel.validateField("sftpPath", validators.validDataDeliveryPath, "Path must only contain allowed parameters: {orgId}, {externalId}, {healthexPatientId}, {referenceId}, {dataType}, {projectId}, {timestamp}");
        }
        if (this.dataDeliverySettingsFormModel.hasErrors())
            return;
        const { deliveryType, deliverUncompressedData, deliverConvertedFHIRData, optedOutOfBackgroundRefresh, s3BucketName, s3AssumeRoleArn, s3BucketPath, s3BucketRegion, sftpHost, sftpPort, sftpUsername, sftpPassword, sftpPath, } = this.dataDeliverySettingsFormModel.input;
        const nullIfEmpty = (value) => (value === "" ? null : value);
        await this.organizationsManager.updateOrganizationDetails(this.organization.id, {
            type: this.organization.type,
            dataDeliverySettings: {
                deliveryType,
                deliverUncompressedData,
                deliverConvertedFHIRData,
                optedOutOfBackgroundRefresh,
                s3Settings: {
                    s3BucketName,
                    s3AssumeRoleArn,
                    s3BucketPath: nullIfEmpty(s3BucketPath),
                    s3BucketRegion: nullIfEmpty(s3BucketRegion),
                },
                sftpSettings: {
                    sftpHost,
                    sftpPort,
                    sftpUsername,
                    sftpPassword,
                    sftpPath: nullIfEmpty(sftpPath),
                },
            },
        });
        await this.organizationData.waitForReload();
        const nowConfigured = ((_d = (_c = this.organization.dataDeliverySettings) === null || _c === void 0 ? void 0 : _c.deliveryType) !== null && _d !== void 0 ? _d : null) !== null;
        this.editingDataDeliverySettings = false;
        if (wasNotConfigured && nowConfigured) {
            this.adminUIManager.openGoLiveChecklistDialogModel();
        }
    }
    get externalDataDeliveryRoleARN() {
        var _a;
        if (this.awsDataDeliveryInfoData.value === NOT_LOADED)
            return NOT_LOADED;
        return (_a = this.awsDataDeliveryInfoData.value.externalDataDeliveryRoleARN) !== null && _a !== void 0 ? _a : null;
    }
}
__decorate([
    observable
], AdminDataSettingsViewModel.prototype, "editingDataAccessPermissions", void 0);
__decorate([
    observable
], AdminDataSettingsViewModel.prototype, "editingDataDeliverySettings", void 0);
__decorate([
    computed
], AdminDataSettingsViewModel.prototype, "organizationId", null);
__decorate([
    computed
], AdminDataSettingsViewModel.prototype, "organization", null);
__decorate([
    computed
], AdminDataSettingsViewModel.prototype, "healthcareOrganization", null);
__decorate([
    computed
], AdminDataSettingsViewModel.prototype, "showDataAccessPermissionsSection", null);
__decorate([
    computed
], AdminDataSettingsViewModel.prototype, "dataAccessPermissionsDescription", null);
__decorate([
    computed
], AdminDataSettingsViewModel.prototype, "dataDeliverySettingsDescription", null);
__decorate([
    computed
], AdminDataSettingsViewModel.prototype, "externalDataDeliveryRoleARN", null);
